import secureLocalStorage from "react-secure-storage";
import * as StorageConstants from "../../Models/SecureStorageConstants";
import type { IKnowledgeArticleCrudModel } from "../../Services/KnowledgeArticleService";
import type { MediaType } from "../../Services/MediaAssetService";
import type { DraftMediaItem, DraftSection } from "./DraftSectionEditor";
import type { SectionType } from "../../Services/KnowledgeArticleSectionService";
import { getStoredUser } from "../../Util/secureStorageSession";

/**
 * Create-mode draft backup. Metadata (titles, HTML, settings, file names) lives in
 * {@code secureLocalStorage} like the rest of the session. File bytes cannot: react-secure-storage
 * encrypts into localStorage (~5–10 MB quota) and a knowledge-base video may be up to 1 GB.
 * Those blobs go to origin-private IndexedDB instead — never uploaded until the user hits Save.
 *
 * A draft belongs to the user who wrote it. Logging out clears secureLocalStorage (and with it the metadata),
 * but not IndexedDB, and a session can also end without a logout — so the draft carries its owner, and one
 * left behind by somebody else on this browser is wiped instead of restored.
 */
const DRAFT_DB_NAME = "dashly-knowledge-article-draft";
const DRAFT_DB_VERSION = 1;
const DRAFT_BLOB_STORE = "files";
const LEGACY_LOCAL_STORAGE_KEY = "kb-new-article-draft";
const DRAFT_PAYLOAD_VERSION = 1;

export interface DraftAttachment {
    localId: string;
    file: File;
}

export interface RestoredKnowledgeArticleDraft {
    model: IKnowledgeArticleCrudModel;
    tagValues: string[];
    selectedCategoryUuids: string[];
    newCategoryNames: string[];
    sections: DraftSection[];
    attachments: DraftAttachment[];
    missingMediaCount: number;
}

interface StoredFileRef {
    localId: string;
    fileName: string;
    fileType: string;
    fileLastModified: number;
    mediaType?: MediaType;
}

interface StoredDraftSection {
    localId: string;
    sectionType: SectionType;
    title: string;
    showTitle: boolean;
    content: string;
    media: StoredFileRef[];
    pendingInlineImageIds: string[];
}

interface KnowledgeArticleDraftPayload {
    version: number;
    /** Who wrote the draft. Absent on drafts stored before this was tracked: those are still restored. */
    userUuid?: string;
    model: IKnowledgeArticleCrudModel;
    tagValues: string[];
    selectedCategoryUuids: string[];
    /** Categories picked by a name that does not exist yet. Absent on drafts stored before these were kept. */
    newCategoryNames?: string[];
    sections: StoredDraftSection[];
    attachments: StoredFileRef[];
}

export const mediaBlobId = (sectionLocalId: string, mediaLocalId: string): string =>
    `media:${sectionLocalId}:${mediaLocalId}`;

export const inlineBlobId = (sectionLocalId: string, pendingId: string): string =>
    `inline:${sectionLocalId}:${pendingId}`;

export const attachmentBlobId = (attachmentLocalId: string): string => `file:${attachmentLocalId}`;

export const collectDraftBlobEntries = (
    sections: DraftSection[],
    attachments: DraftAttachment[]
): { id: string; file: File }[] => {
    const entries: { id: string; file: File }[] = [];
    for (const section of sections) {
        for (const item of section.media) {
            entries.push({ id: mediaBlobId(section.localId, item.localId), file: item.file });
        }
        for (const [pendingId, file] of Object.entries(section.pendingInlineImages)) {
            entries.push({ id: inlineBlobId(section.localId, pendingId), file });
        }
    }
    for (const attachment of attachments) {
        entries.push({ id: attachmentBlobId(attachment.localId), file: attachment.file });
    }
    return entries;
};

export const isKnowledgeArticleDraftEmpty = (
    model: IKnowledgeArticleCrudModel,
    sections: DraftSection[],
    attachments: DraftAttachment[]
): boolean => !model.title.trim() && sections.length === 0 && attachments.length === 0;

export const persistKnowledgeArticleDraftMetadata = (
    model: IKnowledgeArticleCrudModel,
    tagValues: string[],
    selectedCategoryUuids: string[],
    newCategoryNames: string[],
    sections: DraftSection[],
    attachments: DraftAttachment[]
): void => {
    const payload: KnowledgeArticleDraftPayload = {
        version: DRAFT_PAYLOAD_VERSION,
        userUuid: getStoredUser()?.uuid,
        model,
        tagValues,
        selectedCategoryUuids,
        newCategoryNames,
        sections: sections.map((section) => ({
            localId: section.localId,
            sectionType: section.sectionType,
            title: section.title,
            showTitle: section.showTitle,
            content: section.content,
            media: section.media.map((item) => toFileRef(item.localId, item.file, item.mediaType)),
            pendingInlineImageIds: Object.keys(section.pendingInlineImages),
        })),
        attachments: attachments.map((attachment) => toFileRef(attachment.localId, attachment.file)),
    };
    secureLocalStorage.setItem(StorageConstants.KNOWLEDGE_ARTICLE_NEW_DRAFT, JSON.stringify(payload));
};

export const syncDraftBlobs = async (
    entries: { id: string; file: File }[],
    previouslyPersistedIds: Set<string>,
    isCancelled?: () => boolean
): Promise<{ persistedIds: Set<string>; mediaFailed: boolean }> => {
    const neededIds = new Set(entries.map((entry) => entry.id));
    const persistedIds = new Set<string>();
    let mediaFailed = false;

    for (const entry of entries) {
        if (isCancelled?.()) {
            return { persistedIds: previouslyPersistedIds, mediaFailed: false };
        }
        if (previouslyPersistedIds.has(entry.id)) {
            persistedIds.add(entry.id);
            continue;
        }
        try {
            await putDraftBlob(entry.id, entry.file);
            persistedIds.add(entry.id);
        } catch {
            mediaFailed = true;
        }
    }

    if (isCancelled?.()) {
        return { persistedIds: previouslyPersistedIds, mediaFailed: false };
    }

    for (const previousId of previouslyPersistedIds) {
        if (neededIds.has(previousId)) {
            continue;
        }
        try {
            await deleteDraftBlob(previousId);
        } catch {
            // Stale blob left behind is harmless; the next successful clear wipes the store.
        }
    }

    return { persistedIds, mediaFailed };
};

export const restoreKnowledgeArticleDraft = async (): Promise<RestoredKnowledgeArticleDraft | null> => {
    const payload = readDraftPayload();
    if (!payload) {
        return null;
    }
    if (payload.userUuid && payload.userUuid !== getStoredUser()?.uuid) {
        await clearKnowledgeArticleDraft();
        return null;
    }

    let missingMediaCount = 0;
    const sections: DraftSection[] = [];
    for (const stored of payload.sections ?? []) {
        const media: DraftMediaItem[] = [];
        for (const ref of stored.media ?? []) {
            const file = await readDraftFile(mediaBlobId(stored.localId, ref.localId), ref);
            if (!file || !ref.mediaType) {
                missingMediaCount += 1;
                continue;
            }
            media.push({
                localId: ref.localId,
                file,
                mediaType: ref.mediaType,
                previewUrl: URL.createObjectURL(file),
            });
        }

        const pendingInlineImages: Record<string, File> = {};
        let content = stored.content ?? "";
        for (const pendingId of stored.pendingInlineImageIds ?? []) {
            const file = await readDraftFile(inlineBlobId(stored.localId, pendingId), {
                localId: pendingId,
                fileName: `${pendingId}.img`,
                fileType: "",
                fileLastModified: Date.now(),
            });
            if (!file) {
                missingMediaCount += 1;
                continue;
            }
            pendingInlineImages[pendingId] = file;
            content = replacePendingInlineSrc(content, pendingId, URL.createObjectURL(file));
        }

        sections.push({
            localId: stored.localId || crypto.randomUUID(),
            sectionType: stored.sectionType,
            title: stored.title,
            showTitle: stored.showTitle,
            content,
            media,
            pendingInlineImages,
        });
    }

    const attachments: DraftAttachment[] = [];
    for (const ref of payload.attachments ?? []) {
        const file = await readDraftFile(attachmentBlobId(ref.localId), ref);
        if (!file) {
            missingMediaCount += 1;
            continue;
        }
        attachments.push({ localId: ref.localId, file });
    }

    return {
        model: payload.model,
        tagValues: payload.tagValues ?? [],
        selectedCategoryUuids: payload.selectedCategoryUuids ?? [],
        newCategoryNames: payload.newCategoryNames ?? [],
        sections,
        attachments,
        missingMediaCount,
    };
};

export const clearKnowledgeArticleDraft = async (): Promise<void> => {
    try {
        secureLocalStorage.removeItem(StorageConstants.KNOWLEDGE_ARTICLE_NEW_DRAFT);
    } catch {
        // Best-effort metadata cleanup.
    }
    try {
        localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
    } catch {
        // Best-effort leftover from the pre-media draft key.
    }
    try {
        await clearDraftBlobs();
    } catch {
        // Best-effort binary cleanup.
    }
};

const toFileRef = (localId: string, file: File, mediaType?: MediaType): StoredFileRef => ({
    localId,
    fileName: file.name,
    fileType: file.type,
    fileLastModified: file.lastModified,
    mediaType,
});

const readDraftPayload = (): KnowledgeArticleDraftPayload | null => {
    const stored = readSecurePayload();
    if (stored) {
        return stored;
    }
    return readAndConsumeLegacyPayload();
};

const readSecurePayload = (): KnowledgeArticleDraftPayload | null => {
    try {
        const raw = secureLocalStorage.getItem(StorageConstants.KNOWLEDGE_ARTICLE_NEW_DRAFT);
        if (raw == null) {
            return null;
        }
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!parsed || typeof parsed !== "object") {
            return null;
        }
        return parsed as KnowledgeArticleDraftPayload;
    } catch {
        return null;
    }
};

/** Pre-media drafts lived in plain localStorage and only stored titles/HTML. Consume once, then drop. */
const readAndConsumeLegacyPayload = (): KnowledgeArticleDraftPayload | null => {
    try {
        const raw = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
        const parsed = JSON.parse(raw) as {
            model?: IKnowledgeArticleCrudModel;
            tagValues?: string[];
            selectedCategoryUuids?: string[];
            sections?: Array<{
                sectionType: SectionType;
                title: string;
                showTitle: boolean;
                content: string;
            }>;
        };
        if (!parsed?.model) {
            return null;
        }
        return {
            version: DRAFT_PAYLOAD_VERSION,
            model: parsed.model,
            tagValues: parsed.tagValues ?? [],
            selectedCategoryUuids: parsed.selectedCategoryUuids ?? [],
            sections: (parsed.sections ?? []).map((section) => ({
                localId: crypto.randomUUID(),
                sectionType: section.sectionType,
                title: section.title,
                showTitle: section.showTitle,
                content: section.content,
                media: [],
                pendingInlineImageIds: [],
            })),
            attachments: [],
        };
    } catch {
        return null;
    }
};

const replacePendingInlineSrc = (html: string, pendingId: string, url: string): string => {
    const escaped = pendingId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const tagPattern = new RegExp(`<img\\b[^>]*\\bdata-pending-upload-id="${escaped}"[^>]*>`, "gi");
    return html.replace(tagPattern, (tag) => {
        if (/\bsrc="/i.test(tag)) {
            return tag.replace(/\bsrc="[^"]*"/i, `src="${url}"`);
        }
        return tag.replace(/<img\b/i, `<img src="${url}"`);
    });
};

const openDraftDb = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const request = indexedDB.open(DRAFT_DB_NAME, DRAFT_DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(DRAFT_BLOB_STORE)) {
                db.createObjectStore(DRAFT_BLOB_STORE);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("Failed to open knowledge-article draft store"));
    });

const putDraftBlob = async (id: string, file: File): Promise<void> => {
    const db = await openDraftDb();
    try {
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(DRAFT_BLOB_STORE, "readwrite");
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error("Failed to store draft media"));
            tx.objectStore(DRAFT_BLOB_STORE).put(file, id);
        });
    } finally {
        db.close();
    }
};

const deleteDraftBlob = async (id: string): Promise<void> => {
    const db = await openDraftDb();
    try {
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(DRAFT_BLOB_STORE, "readwrite");
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error("Failed to delete draft media"));
            tx.objectStore(DRAFT_BLOB_STORE).delete(id);
        });
    } finally {
        db.close();
    }
};

const clearDraftBlobs = async (): Promise<void> => {
    const db = await openDraftDb();
    try {
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(DRAFT_BLOB_STORE, "readwrite");
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error("Failed to clear draft media"));
            tx.objectStore(DRAFT_BLOB_STORE).clear();
        });
    } finally {
        db.close();
    }
};

const readDraftFile = async (id: string, ref: StoredFileRef): Promise<File | null> => {
    const db = await openDraftDb();
    try {
        const blob = await new Promise<Blob | File | undefined>((resolve, reject) => {
            const tx = db.transaction(DRAFT_BLOB_STORE, "readonly");
            const request = tx.objectStore(DRAFT_BLOB_STORE).get(id);
            request.onsuccess = () => resolve(request.result as Blob | File | undefined);
            request.onerror = () => reject(request.error ?? new Error("Failed to read draft media"));
        });
        if (!blob) {
            return null;
        }
        if (blob instanceof File && blob.name) {
            return blob;
        }
        return new File([blob], ref.fileName || "file", {
            type: ref.fileType || blob.type,
            lastModified: ref.fileLastModified || Date.now(),
        });
    } catch {
        return null;
    } finally {
        db.close();
    }
};
